import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ChangeEvent as ReactChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Minus, Play, Plus, Square } from "lucide-react";
import { DEMO, DEMO_LENGTH } from "@/lib/audio/demo";
import { GloamEngine, type HornMeter } from "@/lib/audio/engine";
import { breathWithPressure, characterFromCc, HornPerformance, midiBreathScale, noteLegato } from "@/lib/audio/performance";
import {
  SETTINGS_KEY,
  expressionForPreset,
  validateSettings,
  type PlayMode,
  type PresetFavorites,
  type VoiceMode,
} from "@/lib/audio/settings";
import {
  codeForMidi,
  isWhite,
  keyLabel,
  midiForCode,
  midiToFreq,
  noteName,
  OCTAVE_BASES,
  whitesBefore,
} from "@/lib/audio/keys";
import { PRESETS, PRESET_ORDER, SHADE_HINT, SHADE_HOME, SHADE_LABEL, type HornId } from "@/lib/audio/presets";
import {
  installAudioUnlock,
  isActivatableTarget,
  isArrowKeyTarget,
  isTextEntryTarget,
  onFocusLost,
  readStored,
  writeStored,
} from "@/lib/browser-hygiene";

/** Below this width the keyboard shows one octave (C to C) that fits a phone, with octave arrows. */
const NARROW_QUERY = "(max-width: 639px)";
const NARROW_STARTS = [36, 48, 60, 72] as const;

type MidiStatus =
  | { state: "off" }
  | { state: "pending" }
  | { state: "on"; names: string[] }
  | { state: "denied" };

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

function captionFor(meter: HornMeter) {
  if (meter.stage === "idle" || meter.level < 0.008) return "Quiet";
  if (meter.stage === "close" || meter.stage === "ring") return "Release — the bore is ringing";
  if (meter.stage === "slur" || meter.slur > 0.08) return "Slur — finding the next slot";
  if (meter.stage === "attack") return "Entrance";
  if (meter.effort - meter.bloom > 0.14) return "Swell — the color is opening";
  return "Sustaining";
}

const Keyboard = memo(function Keyboard({
  base,
  start,
  count,
  active,
  onNoteDown,
  onKeyNote,
}: {
  /** Base of the computer-keyboard mapping (for the letter labels). */
  base: number;
  /** First and number of on-screen keys. */
  start: number;
  count: number;
  active: ReadonlySet<number>;
  onNoteDown: (midi: number, pointerId: number) => void;
  /** Keyboard / assistive-tech activation of an on-screen key. */
  onKeyNote: (midi: number, down: boolean, source: string) => void;
}) {
  const pulseTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const pulseId = useRef(0);
  /** Time of the last pointer down/up on the keys (see click()). */
  const lastPointer = useRef(-Infinity);
  useEffect(() => {
    const timers = pulseTimers.current;
    return () => {
      for (const [source, timer] of timers) {
        clearTimeout(timer);
        onKeyNote(start, false, source);
      }
      timers.clear();
      for (let midi = start; midi < start + count; midi++) {
        onKeyNote(midi, false, `ui:${midi}:Enter`);
        onKeyNote(midi, false, `ui:${midi}:Space`);
      }
    };
  }, [start, count, onKeyNote]);
  const midis = Array.from({ length: count }, (_, i) => start + i);
  const whites = midis.filter(isWhite);
  const blacks = midis.filter((midi) => !isWhite(midi));
  const whiteCount = whites.length;
  const play = (midi: number) => (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    onNoteDown(midi, event.pointerId);
  };
  // Enter/Space on a focused key holds the note like a real key; preventDefault stops the
  // synthetic click so it does not play twice.
  const keyDown = (midi: number) => (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    event.preventDefault();
    if (!event.repeat) onKeyNote(midi, true, `ui:${midi}:${event.key === " " ? "Space" : "Enter"}`);
  };
  const keyUp = (midi: number) => (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onKeyNote(midi, false, `ui:${midi}:${event.key === " " ? "Space" : "Enter"}`);
  };
  // A click with detail 0 comes from assistive tech (no pointer, no key events): play a short note.
  // Touch taps can also produce a detail-0 click right after pointerup (seen in Chromium); that
  // one must not re-strike the note the finger just released, so clicks that follow a pointer
  // gesture on the keys are ignored.
  const click = (midi: number) => (event: React.MouseEvent<HTMLButtonElement>) => {
    if (event.detail !== 0 || performance.now() - lastPointer.current < 1000) return;
    const source = `assistive:${++pulseId.current}`;
    onKeyNote(midi, true, source);
    pulseTimers.current.set(
      source,
      setTimeout(() => {
        onKeyNote(midi, false, source);
        pulseTimers.current.delete(source);
      }, 450),
    );
  };
  const handlers = (midi: number) => ({
    onPointerDown: play(midi),
    onKeyDown: keyDown(midi),
    onKeyUp: keyUp(midi),
    onClick: click(midi),
    onBlur: () => {
      onKeyNote(midi, false, `ui:${midi}:Enter`);
      onKeyNote(midi, false, `ui:${midi}:Space`);
    },
  });
  const markPointer = () => {
    lastPointer.current = performance.now();
  };
  return (
    <div
      className="keyboard"
      onPointerDownCapture={markPointer}
      onPointerUpCapture={markPointer}
      onPointerCancelCapture={markPointer}
    >
      {whites.map((midi) => {
        const code = codeForMidi(midi, base);
        return (
          <button
            key={midi}
            type="button"
            className="key-white"
            data-midi={midi}
            data-kind="white"
            data-active={active.has(midi) ? "true" : "false"}
            aria-pressed={active.has(midi)}
            aria-label={`${noteName(midi)}${code ? `, key ${keyLabel(code)}` : ""}`}
            {...handlers(midi)}
          >
            <span className="key-note">{noteName(midi)}</span>
            <span className="key-bind">{keyLabel(code)}</span>
          </button>
        );
      })}
      {blacks.map((midi) => {
        const code = codeForMidi(midi, base);
        const before = whitesBefore(midi, start);
        const left = `calc(${before} * 100% / ${whiteCount} - (100% / ${whiteCount} * 0.31))`;
        const width = `calc(100% / ${whiteCount} * 0.62)`;
        return (
          <button
            key={midi}
            type="button"
            className="key-black"
            style={{ left, width }}
            data-midi={midi}
            data-kind="black"
            data-active={active.has(midi) ? "true" : "false"}
            aria-pressed={active.has(midi)}
            aria-label={`${noteName(midi)}${code ? `, key ${keyLabel(code)}` : ""}`}
            {...handlers(midi)}
          >
            <span className="key-note">{NAMES_ONLY(midi)}</span>
            <span className="key-bind">{keyLabel(code)}</span>
          </button>
        );
      })}
    </div>
  );
});

function NAMES_ONLY(midi: number) {
  return noteName(midi).replace(/\d+$/, "");
}

function midiFromPoint(x: number, y: number) {
  const stack = document.elementsFromPoint(x, y);
  for (const el of stack) {
    if (!(el instanceof HTMLElement)) continue;
    const key = el.closest("[data-midi]");
    if (key instanceof HTMLElement && key.dataset.midi) return Number(key.dataset.midi);
  }
  return null;
}

function useNarrow() {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return narrow;
}

function PlayModeToggle({
  mode,
  onChange,
}: {
  mode: PlayMode;
  onChange: (mode: PlayMode) => void;
}) {
  return (
    <div className="voice-toggle" role="radiogroup" aria-label="Play mode">
      {(["mono", "poly"] as const).map((next) => {
        const on = mode === next;
        return (
          <button
            key={next}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={on ? 0 : -1}
            data-play-mode={next}
            className={on ? "voice-opt voice-opt-on" : "voice-opt"}
            title={
              next === "mono"
                ? "One note, the way the horn plays now."
                : "Up to four notes, on either lip. Each note is its own lip and bore. Air, mute, character and bend stay shared, and the oldest note gives way."
            }
            onClick={() => onChange(next)}
            onKeyDown={(event) => {
              if (
                event.key !== "ArrowLeft" &&
                event.key !== "ArrowRight" &&
                event.key !== "ArrowUp" &&
                event.key !== "ArrowDown"
              )
                return;
              event.preventDefault();
              const other = next === "mono" ? "poly" : "mono";
              onChange(other);
              event.currentTarget.parentElement
                ?.querySelector<HTMLButtonElement>(`[data-play-mode="${other}"]`)
                ?.focus();
            }}
          >
            {next === "mono" ? "Mono" : "Poly"}
          </button>
        );
      })}
    </div>
  );
}

function characterReadout(value: number) {
  if (Math.abs(value) < 0.005) return "Center";
  if (value < 0) return `Horn ${Math.round(-value * 100)}`;
  return `Bass ${Math.round(value * 100)}`;
}

function shadeReadout(value: number) {
  if (Math.abs(value - SHADE_HOME) < 0.005) return "Home";
  return String(Math.round(value * 100));
}

function ShadeControl({
  presetId,
  value,
  onChange,
}: {
  presetId: HornId;
  value: number;
  onChange: (value: number) => void;
}) {
  const label = SHADE_LABEL[presetId];
  const readout = shadeReadout(value);
  return (
    <label className="shade-control">
      {label} <output>{readout}</output>
      <input
        id="gloam-shade"
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        aria-label={`${label} for ${PRESETS[presetId].name}. Home is this bore's sound.`}
        aria-valuetext={`${label} ${readout}`}
        title={SHADE_HINT[presetId]}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function CharacterControl({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  const readout = characterReadout(value);
  return (
    <label className="character-control">
      Character <output>{readout}</output>
      <input
        id="gloam-character"
        type="range"
        min={-1}
        max={1}
        step={0.01}
        value={value}
        aria-label="Character, from horn to bass. Center is today's lip."
        aria-valuetext={readout}
        title="Horn lengthens the bore and opens the flare. Bass is heavier and darker. Center is today's lip. The placed lip ignores this. MIDI CC21: 0 horn, 64 center, 127 bass."
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function LegatoToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div className="voice-toggle">
      <button
        type="button"
        className={on ? "voice-opt voice-opt-on" : "voice-opt"}
        aria-pressed={on}
        title={on ? "Legato on — notes slur into each other" : "Legato off — each note starts again"}
        onClick={onToggle}
      >
        Legato
      </button>
    </div>
  );
}

export function GloamInstrument() {
  const [initial] = useState(() => validateSettings(readStored(SETTINGS_KEY)));
  const engineRef = useRef<GloamEngine | null>(null);
  if (!engineRef.current) {
    const created = new GloamEngine();
    created.setVoiceMode(initial.voiceMode);
    created.setPlayMode(initial.playMode);
    created.setCharacter(initial.character);
    created.setShade(initial.shade);
    engineRef.current = created;
  }
  const engine = engineRef.current;

  const [presetId, setPresetId] = useState<HornId>(initial.presetId);
  const [favorites, setFavorites] = useState<PresetFavorites>(initial.favorites);
  const [voiceMode, setVoiceMode] = useState<VoiceMode>(initial.voiceMode);
  const [legato, setLegato] = useState(initial.legato);
  const [playMode, setPlayMode] = useState<PlayMode>(initial.playMode);
  const [character, setCharacter] = useState(initial.character);
  const [shade, setShade] = useState(initial.shade);
  const [effort, setEffort] = useState(initial.effort);
  const [mute, setMute] = useState(initial.mute);
  const [volume, setVolume] = useState(initial.volume);
  const [storageAvailable, setStorageAvailable] = useState(true);
  const [base, setBase] = useState<number>(initial.base);
  const [active, setActive] = useState<ReadonlySet<number>>(new Set());
  const [caption, setCaption] = useState("Set the air, then play.");
  const [slur, setSlur] = useState(false);
  const [demoOn, setDemoOn] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);
  const narrow = useNarrow();
  const [narrowStart, setNarrowStart] = useState<number>(initial.narrowStart);
  const [midiSupported, setMidiSupported] = useState(false);
  const [midi, setMidi] = useState<MidiStatus>({ state: "off" });

  const effortRef = useRef(effort);
  /** MIDI channel pressure (0..1). It adds breath on top of the slider/CC air instead of replacing it. */
  const pressureRef = useRef(0);
  const [pressure, setPressure] = useState(0);
  const muteRef = useRef(mute);
  const baseRef = useRef(base);
  const performanceRef = useRef(new HornPerformance());
  const noteRevision = useRef(0);
  const demoRevision = useRef(0);
  const mounted = useRef(true);
  const savedSettings = useRef(initial);
  const slurRef = useRef(false);
  const demoStop = useRef<(() => void) | null>(null);
  const caseRef = useRef<HTMLElement>(null);
  /** Touch octave buttons act on pointerup; the click that follows must not step twice. */
  const octaveFromTouch = useRef(false);
  const armOctaveTouch = () => {
    octaveFromTouch.current = true;
    window.setTimeout(() => {
      octaveFromTouch.current = false;
    }, 700);
  };
  /** Computer key code -> the note it started, so keyup releases that note even after an octave shift. */
  const keyNotes = useRef<Map<string, number>>(new Map());
  /** Active on-screen glides, one per finger/pointer. */
  const glides = useRef<Map<number, () => void>>(new Map());
  const midiAccess = useRef<MIDIAccess | null>(null);
  const midiHandler = useRef<(data: Uint8Array, device: string) => void>(() => {});
  const boundInputs = useRef(new Map<string, MIDIInput>());
  const presetRef = useRef(presetId);
  const favoritesRef = useRef(favorites);
  const voiceModeRef = useRef<VoiceMode>(voiceMode);
  const legatoRef = useRef(legato);
  const playModeRef = useRef(playMode);
  const characterRef = useRef(character);
  const shadeRef = useRef(shade);
  /** Sources the poly pool is currently holding. Mono ignores this. */
  const polySent = useRef(new Set<string>());
  /** Latest pitch-bend wheel, applied to every poly voice. Mono keeps per-channel bend. */
  const wheelBend = useRef(0);
  effortRef.current = effort;
  muteRef.current = mute;
  baseRef.current = base;
  presetRef.current = presetId;
  favoritesRef.current = favorites;
  voiceModeRef.current = voiceMode;
  legatoRef.current = legato;
  playModeRef.current = playMode;
  characterRef.current = character;
  shadeRef.current = shade;

  const wake = useCallback(async () => {
    try {
      await engine.ensure();
      if (!mounted.current) return false;
      setAudioError(null);
      setAudioReady(true);
      return true;
    } catch {
      if (!mounted.current) return false;
      setAudioError(
        "Audio could not start. Use HTTPS or localhost and an AudioWorklet-capable browser, then retry.",
      );
      return false;
    }
  }, [engine]);

  const stopDemo = useCallback(() => {
    ++demoRevision.current;
    demoStop.current?.();
    demoStop.current = null;
    setDemoOn(false);
  }, []);

  const syncActive = useCallback(() => {
    setActive(performanceRef.current.pitches);
  }, []);

  /** The breath the horn actually gets: slider/CC air, raised toward full by channel pressure. */
  const airOut = () => breathWithPressure(effortRef.current, pressureRef.current);

  const setPressureAir = useCallback(
    (value: number) => {
      const p = clamp(value, 0, 1);
      if (p === pressureRef.current) return;
      pressureRef.current = p;
      setPressure(p);
      engine.setEffort(breathWithPressure(effortRef.current, p));
    },
    [engine],
  );

  const soundCurrent = useCallback(
    (legato = true) => {
      const model = performanceRef.current;
      const note = model.current;
      const revision = ++noteRevision.current;
      syncActive();
      if (!note) {
        // Some keyboards never send pressure 0; never let it carry into the next phrase.
        setPressureAir(0);
        engine.release();
        engine.setBend(0);
        return;
      }
      const send = (fresh = false) => {
        engine.setVoiceMode(voiceModeRef.current);
        engine.setPreset(PRESETS[presetRef.current]);
        engine.setEffort(airOut());
        engine.setMute(muteRef.current);
        engine.setCharacter(characterRef.current);
        engine.setShade(shadeRef.current);
        engine.setBend(fresh ? 0 : model.bend);
        engine.noteOn(midiToFreq(note.midi), fresh ? false : legato, note.velocity, note.breathScale);
      };
      if (engine.node && engine.ctx?.state === "running") send();
      else
        void wake().then((ok) => {
          if (!ok || !mounted.current) return;
          if (revision === noteRevision.current && model.current === note) {
            send();
            return;
          }
          // Cold start: the note was tapped and already released while audio was still booting
          // (first touch on a phone, uncached worklet). Let it speak briefly instead of
          // swallowing the player's first note, unless anything else happened since.
          if (noteRevision.current !== revision + 1 || model.current) return;
          const blip = noteRevision.current;
          send(true);
          window.setTimeout(() => {
            if (mounted.current && noteRevision.current === blip && !model.current)
              engine.release();
          }, 160);
        });
    },
    [engine, setPressureAir, syncActive, wake],
  );

  const soundPoly = useCallback(
    (source: string, legato = false) => {
      const model = performanceRef.current;
      const note = model.noteOf(source);
      const revision = ++noteRevision.current;
      syncActive();
      if (!note) return;
      const send = (fresh = false) => {
        engine.setVoiceMode(voiceModeRef.current);
        engine.setPlayMode("poly");
        engine.setPreset(PRESETS[presetRef.current]);
        engine.setEffort(airOut());
        engine.setMute(muteRef.current);
        engine.setCharacter(characterRef.current);
        engine.setShade(shadeRef.current);
        engine.setBend(fresh ? 0 : wheelBend.current);
        engine.noteOnId(
          source,
          midiToFreq(note.midi),
          fresh ? false : legato,
          note.velocity,
          note.breathScale,
        );
        polySent.current.add(source);
      };
      if (engine.node && engine.ctx?.state === "running") send();
      else
        void wake().then((ok) => {
          if (!ok || !mounted.current) return;
          if (playModeRef.current !== "poly") return;
          // Every note still held should speak, including a chord that landed
          // before the worklet finished loading. A glide replaces the note object.
          if (model.noteOf(source) === note) {
            send();
            return;
          }
          // Same cold-start blip as mono: a tap that ended before the worklet loaded
          // still speaks, unless a newer gesture has already taken over.
          if (noteRevision.current !== revision + 1 || model.current) return;
          const blip = noteRevision.current;
          send(true);
          window.setTimeout(() => {
            if (mounted.current && noteRevision.current === blip && !model.noteOf(source)) {
              engine.releaseId(source);
              polySent.current.delete(source);
            }
          }, 160);
        });
    },
    [engine, syncActive, wake],
  );

  /** Drop poly voices whose owners are gone. `cut` kills the bore tail (CC120). */
  const dropMissingPoly = useCallback(
    (cut: boolean) => {
      const model = performanceRef.current;
      for (const id of [...polySent.current]) {
        if (model.noteOf(id)) continue;
        if (cut) engine.silenceId(id);
        else engine.releaseId(id);
        polySent.current.delete(id);
      }
      if (!model.current) {
        setPressureAir(0);
        engine.setBend(0);
      }
      syncActive();
    },
    [engine, setPressureAir, syncActive],
  );

  const pressNote = useCallback(
    (
      midi: number,
      source: string,
      fromGlide = false,
      velocity?: number,
      scope?: string,
      breathScale?: number,
    ) => {
      stopDemo();
      const model = performanceRef.current;
      if (playModeRef.current === "poly") {
        // Legato is per voice: a glide or Space connects that finger's note.
        // A new chord tone has no previous note on its voice, so it attacks.
        const prev = model.noteOf(source);
        const slur = noteLegato(legatoRef.current, prev, midi, slurRef.current || fromGlide);
        model.down(source, midi, velocity ?? effortRef.current, scope, breathScale ?? 1);
        soundPoly(source, slur);
        return;
      }
      // Legato on: a different pitch slurs, the same pitch re-tongues, and Space or a glide
      // can force a slur. Legato off: every new note, including an overlap, re-attacks.
      const slur = noteLegato(legatoRef.current, model.current, midi, slurRef.current || fromGlide);
      model.down(source, midi, velocity ?? effortRef.current, scope, breathScale ?? 1);
      soundCurrent(slur);
    },
    [soundCurrent, soundPoly, stopDemo],
  );

  const releaseNote = useCallback(
    (source: string) => {
      const model = performanceRef.current;
      if (playModeRef.current === "poly") {
        ++noteRevision.current;
        model.up(source);
        if (!model.noteOf(source)) {
          engine.releaseId(source);
          polySent.current.delete(source);
        }
        if (!model.current) {
          setPressureAir(0);
          engine.setBend(0);
        }
        syncActive();
        return;
      }
      const previous = model.current;
      model.up(source);
      if (model.current !== previous) soundCurrent(legatoRef.current);
      else syncActive();
    },
    [engine, setPressureAir, soundCurrent, syncActive],
  );

  /** Cancel pending starts as well as every held note and demo. */
  const releaseAll = useCallback(() => {
    stopDemo();
    ++noteRevision.current;
    performanceRef.current.clear();
    polySent.current.clear();
    for (const finish of [...glides.current.values()]) finish();
    glides.current.clear();
    keyNotes.current.clear();
    slurRef.current = false;
    setSlur(false);
    setPressureAir(0);
    engine.setBend(0);
    engine.panic();
    syncActive();
  }, [engine, setPressureAir, stopDemo, syncActive]);

  const applyAir = useCallback(
    (nextEffort: number, nextMute: number) => {
      const e = clamp(nextEffort, 0, 1);
      const m = clamp(nextMute, 0, 1);
      effortRef.current = e;
      muteRef.current = m;
      setEffort(e);
      setMute(m);
      engine.setEffort(breathWithPressure(e, pressureRef.current));
      engine.setMute(m);
    },
    [engine],
  );

  /** Horn −1, today's lip at 0, bass +1. Preset changes do not call this. */
  const applyCharacter = useCallback(
    (value: number) => {
      const next = clamp(value, -1, 1);
      characterRef.current = next;
      setCharacter(next);
      engine.setCharacter(next);
    },
    [engine],
  );

  /** The current bore's own lip control. Home (0.62) is that bore's sound. */
  const applyShade = useCallback(
    (value: number) => {
      const next = clamp(value, 0, 1);
      shadeRef.current = next;
      setShade(next);
      engine.setShade(next);
    },
    [engine],
  );

  /** Arrow keys: up/down = air, left/right = mute. Holding a key repeats, so it sweeps. */
  const nudgeAir = useCallback(
    (code: string) => {
      const de = code === "ArrowUp" ? 0.03 : code === "ArrowDown" ? -0.03 : 0;
      const dm = code === "ArrowRight" ? 0.03 : code === "ArrowLeft" ? -0.03 : 0;
      if (!de && !dm) return false;
      stopDemo();
      applyAir(effortRef.current + de, muteRef.current + dm);
      void wake();
      return true;
    },
    [applyAir, stopDemo, wake],
  );

  useEffect(() => {
    const node = caseRef.current;
    let frame = 0;
    const time = new Uint8Array(1024);
    const freq = new Uint8Array(512);
    const loop = () => {
      const analyser = engine.analyser;
      if (analyser && node) {
        analyser.getByteTimeDomainData(time);
        let sum = 0;
        for (let i = 0; i < time.length; i++) {
          const x = (time[i] - 128) / 128;
          sum += x * x;
        }
        const rms = Math.sqrt(sum / time.length);
        analyser.getByteFrequencyData(freq);
        let num = 0;
        let den = 0;
        for (let i = 2; i < freq.length; i++) {
          num += i * freq[i];
          den += freq[i];
        }
        const bright = den > 8 ? clamp((num / den / freq.length) * 2.4, 0, 1) : 0;
        node.style.setProperty("--speak", rms.toFixed(3));
        node.style.setProperty("--bright", bright.toFixed(3));
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [engine]);

  useEffect(() => {
    mounted.current = true;
    const unlock = installAudioUnlock(() => engine.ctx);
    engine.onError(() => {
      releaseAll();
      setAudioReady(false);
      setAudioError("The audio processor stopped. Retry audio to restart the horn.");
    });
    engine.onMeter((meter) => {
      setSpeaking(meter.level > 0.012 && meter.stage !== "idle");
      if (demoStop.current) return;
      const next = captionFor(meter);
      setCaption((current) => (current === next ? current : next));
    });
    return () => {
      mounted.current = false;
      ++noteRevision.current;
      ++demoRevision.current;
      demoStop.current?.();
      unlock();
      engine.onMeter(null);
      engine.onError(null);
      void engine.close();
    };
  }, [engine, releaseAll]);

  useEffect(() => {
    engine.setVolume(volume);
  }, [engine, volume]);

  useEffect(() => {
    engine.setVoiceMode(voiceMode);
  }, [engine, voiceMode]);

  useEffect(() => {
    engine.setPlayMode(playMode);
  }, [engine, playMode]);

  useEffect(() => {
    if (demoOn) return;
    savedSettings.current = {
      version: 1,
      presetId,
      voiceMode,
      legato,
      playMode,
      character,
      shade,
      effort,
      mute,
      volume,
      base,
      narrowStart,
      favorites,
    };
    const timer = window.setTimeout(
      () => setStorageAvailable(writeStored(SETTINGS_KEY, savedSettings.current)),
      300,
    );
    return () => window.clearTimeout(timer);
  }, [presetId, voiceMode, legato, playMode, character, shade, effort, mute, volume, base, narrowStart, favorites, demoOn]);

  useEffect(() => {
    const save = () => {
      writeStored(SETTINGS_KEY, savedSettings.current);
    };
    window.addEventListener("pagehide", save);
    return () => {
      window.removeEventListener("pagehide", save);
      save();
    };
  }, []);

  useEffect(() => onFocusLost(releaseAll), [releaseAll]);

  useEffect(() => {
    setMidiSupported(typeof navigator !== "undefined" && "requestMIDIAccess" in navigator);
    return () => {
      if (midiAccess.current) midiAccess.current.onstatechange = null;
      boundInputs.current.forEach((input) => {
        input.onmidimessage = null;
      });
      boundInputs.current.clear();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (event.code === "Escape") {
        event.preventDefault();
        releaseAll();
        return;
      }
      if (isTextEntryTarget(target)) return;
      if (event.code === "Space") {
        // Buttons, piano keys, and the air/mute sliders keep Space. Anywhere else, Space holds a slur.
        if (isActivatableTarget(target)) return;
        event.preventDefault();
        if (event.repeat) return;
        slurRef.current = true;
        setSlur(true);
        return;
      }
      if (event.code.startsWith("Arrow")) {
        if (isArrowKeyTarget(target)) return;
        if (nudgeAir(event.code)) event.preventDefault();
        return;
      }
      if (event.code === "Minus" || event.code === "Equal") {
        event.preventDefault();
        setBase((current) => {
          const index = OCTAVE_BASES.indexOf(current as (typeof OCTAVE_BASES)[number]);
          const next =
            event.code === "Minus"
              ? Math.max(0, index - 1)
              : Math.min(OCTAVE_BASES.length - 1, index + 1);
          return OCTAVE_BASES[next] ?? current;
        });
        return;
      }
      const midi = midiForCode(event.code, baseRef.current);
      if (midi == null) return;
      event.preventDefault();
      if (event.repeat || keyNotes.current.has(event.code)) return;
      keyNotes.current.set(event.code, midi);
      void pressNote(midi, `computer:${event.code}`);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === "Space") {
        slurRef.current = false;
        setSlur(false);
        return;
      }
      const midi = keyNotes.current.get(event.code);
      if (midi == null) return;
      keyNotes.current.delete(event.code);
      releaseNote(`computer:${event.code}`);
    };
    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [nudgeAir, pressNote, releaseAll, releaseNote]);

  const onSliderPointerDown = (event: ReactPointerEvent<HTMLInputElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    // The slider is not a note source. Start audio in the gesture and leave held notes alone.
    stopDemo();
    void wake();
  };

  const onAirSlider = (event: ReactChangeEvent<HTMLInputElement>) => {
    stopDemo();
    applyAir(Number(event.target.value), muteRef.current);
    void wake();
  };

  const onMuteSlider = (event: ReactChangeEvent<HTMLInputElement>) => {
    stopDemo();
    applyAir(effortRef.current, Number(event.target.value));
    void wake();
  };

  const beginGlide = useCallback(
    (midi: number, pointerId: number) => {
      glides.current.get(pointerId)?.();
      let current: number | null = midi;
      const source = `pointer:${pointerId}`;
      pressNote(midi, source);
      // Hit-test under the pointer: touch pointers are implicitly captured by the first key, so
      // event.target would stay on it and a finger could never glide to the next key.
      const move = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) return;
        const next = midiFromPoint(event.clientX, event.clientY);
        if (next == null || next === current) return;
        current = next;
        pressNote(next, source, true);
      };
      const up = (event: PointerEvent) => {
        if (event.pointerId !== pointerId) return;
        finish();
      };
      const finish = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);
        if (glides.current.get(pointerId) === finish) glides.current.delete(pointerId);
        const held = current;
        current = null;
        if (held != null) releaseNote(source);
      };
      glides.current.set(pointerId, finish);
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", up);
    },
    [pressNote, releaseNote],
  );

  const keyNote = useCallback(
    (midi: number, down: boolean, source: string) => {
      if (down) pressNote(midi, source);
      else releaseNote(source);
    },
    [pressNote, releaseNote],
  );

  useEffect(() => {
    const active = glides.current;
    return () => {
      for (const finish of [...active.values()]) finish();
    };
  }, []);

  // MIDI note/pedal/bend ownership is per device and channel. Breath remains a shared air control.
  midiHandler.current = (data: Uint8Array, device: string) => {
    if (document.visibilityState === "hidden") return;
    const status = data[0] ?? 0;
    if (status < 0x80 || status >= 0xf0) return;
    const d1 = data[1] ?? 0;
    const d2 = data[2] ?? 0;
    const type = status & 0xf0;
    if (data.length < (type === 0xd0 || type === 0xc0 ? 2 : 3) || d1 > 127 || d2 > 127) return;
    const scope = `midi:${encodeURIComponent(device)}:${status & 0x0f}`;
    const source = `${scope}:${d1}`;
    const model = performanceRef.current;
    const before = model.current;
    if (type === 0x90 && d2 > 0) {
      const vel = d2 / 127;
      pressNote(d1, source, false, vel, scope, midiBreathScale(vel));
    }
    else if (type === 0x80 || type === 0x90) releaseNote(source);
    else if (type === 0xb0) {
      if (d1 === 2 || d1 === 11) {
        stopDemo();
        applyAir(d2 / 127, muteRef.current);
      } else if (d1 === 1) {
        stopDemo();
        applyAir(effortRef.current, d2 / 127);
      } else if (d1 === 21) {
        // Free controller. CC1 is mute, CC2/CC11 are air, pressure adds air.
        stopDemo();
        applyCharacter(characterFromCc(d2));
      } else if (d1 === 64) model.sustain(scope, d2 >= 64);
      else if (d1 === 120 || d1 === 123) {
        model.clearScope(scope);
        if (playModeRef.current !== "poly" && d1 === 120 && before?.scope === scope) engine.panic();
      } else if (d1 === 121) {
        model.resetControllers(scope);
        if (playModeRef.current === "poly") wheelBend.current = 0;
      }
      if (playModeRef.current === "poly") {
        if (d1 === 64 || d1 === 120 || d1 === 121 || d1 === 123) dropMissingPoly(d1 === 120);
        else syncActive();
        if (d1 === 121) engine.setBend(0);
      } else {
        if (model.current !== before) soundCurrent(legatoRef.current);
        else syncActive();
        engine.setBend(model.bend);
      }
    } else if (type === 0xd0) {
      // Channel pressure pushes extra air; letting go returns to the slider/CC breath. (Mapping it
      // straight to air made a keyboard's pressure-0 message choke the horn.)
      stopDemo();
      setPressureAir(d1 / 127);
    } else if (type === 0xe0) {
      const cents = ((((d2 << 7) | d1) - 8192) / 8192) * 200;
      model.setBend(scope, cents);
      if (playModeRef.current === "poly") {
        wheelBend.current = cents;
        engine.setBend(cents);
      } else engine.setBend(model.bend);
    }
  };

  const connectMidi = async () => {
    if (!("requestMIDIAccess" in navigator) || midi.state === "pending") return;
    setMidi({ state: "pending" });
    void wake();
    try {
      const access = await navigator.requestMIDIAccess({ sysex: false });
      if (!mounted.current) return;
      midiAccess.current = access;
      const bind = () => {
        const names: string[] = [];
        const connected = new Set<string>();
        access.inputs.forEach((input) => {
          if (input.state === "disconnected") return;
          connected.add(input.id);
          boundInputs.current.set(input.id, input);
          input.onmidimessage = (event) => {
            if (event.data) midiHandler.current(event.data, input.id);
          };
          names.push(input.name || "MIDI input");
        });
        const model = performanceRef.current;
        const before = model.current;
        for (const [id, input] of boundInputs.current) {
          if (connected.has(id)) continue;
          input.onmidimessage = null;
          model.clearScope(`midi:${encodeURIComponent(id)}`);
          boundInputs.current.delete(id);
        }
        if (playModeRef.current === "poly") dropMissingPoly(false);
        else {
          if (before !== model.current) soundCurrent(legatoRef.current);
          else syncActive();
          engine.setBend(model.bend);
        }
        setMidi({ state: "on", names });
      };
      access.onstatechange = bind;
      bind();
    } catch {
      if (mounted.current) setMidi({ state: "denied" });
    }
  };

  const choosePreset = (id: HornId) => {
    stopDemo();
    presetRef.current = id;
    setPresetId(id);
    const preset = PRESETS[id];
    const home = expressionForPreset(id, favoritesRef.current);
    applyAir(home.effort, home.mute);
    applyShade(home.shade);
    engine.setPreset(preset);
  };

  const saveFavorite = () => {
    const id = presetRef.current;
    const next: PresetFavorites = {
      ...favoritesRef.current,
      [id]: { effort: effortRef.current, mute: muteRef.current, shade: shadeRef.current },
    };
    favoritesRef.current = next;
    setFavorites(next);
  };

  const recallFavorite = () => {
    const home = favoritesRef.current[presetRef.current];
    if (!home) return;
    stopDemo();
    applyAir(home.effort, home.mute);
    applyShade(home.shade);
  };

  const resetExpression = () => {
    stopDemo();
    const preset = PRESETS[presetRef.current];
    applyAir(preset.homeEffort, preset.homeMute);
    applyCharacter(0);
    applyShade(SHADE_HOME);
    engine.setPreset(preset);
  };

  const chooseVoiceMode = (mode: VoiceMode) => {
    voiceModeRef.current = mode;
    setVoiceMode(mode);
    engine.setVoiceMode(mode);
  };

  const chooseLegato = () => {
    const next = !legatoRef.current;
    legatoRef.current = next;
    setLegato(next);
  };

  const choosePlayMode = (mode: PlayMode) => {
    if (mode === playModeRef.current) return;
    playModeRef.current = mode;
    setPlayMode(mode);
    engine.setPlayMode(mode);
    engine.panic();
    polySent.current.clear();
    ++noteRevision.current;
    const model = performanceRef.current;
    if (!model.current) {
      engine.setBend(0);
      syncActive();
      return;
    }
    // Retrigger what this mode should be sounding so a held chord cannot stick
    // on a voice the new mode will never release.
    if (mode === "mono") soundCurrent(false);
    else {
      wheelBend.current = model.bend;
      engine.setBend(model.bend);
      for (const note of model.list()) soundPoly(note.source, false);
    }
  };

  const playDemo = () => {
    if (demoOn) {
      stopDemo();
      setDemoOn(false);
      return;
    }
    releaseAll();
    const revision = ++demoRevision.current;
    const home = { effort: effortRef.current, mute: muteRef.current };
    setDemoOn(true);
    void (async () => {
      const ok = await wake();
      if (!ok || revision !== demoRevision.current || !mounted.current) {
        if (mounted.current && revision === demoRevision.current) setDemoOn(false);
        return;
      }
      const preset = PRESETS[presetRef.current];
      engine.setVoiceMode(voiceModeRef.current);
      engine.setPreset(preset);
      const ctx = engine.ctx;
      if (!ctx) return;
      const t0 = ctx.currentTime + 0.06;
      const cues = DEMO.map((cue) => ({ ...cue, done: false }));
      let effortNow = effortRef.current;
      let muteNow = muteRef.current;
      let ramp = {
        fromE: effortNow,
        fromM: muteNow,
        toE: effortNow,
        toM: muteNow,
        at: 0,
        dur: 0.01,
      };
      let frame = 0;
      let stopped = false;
      const finish = () => {
        if (stopped) return;
        stopped = true;
        cancelAnimationFrame(frame);
        demoStop.current = null;
        engine.release();
        if (mounted.current) {
          setActive(new Set());
          applyAir(home.effort, home.mute);
          setDemoOn(false);
        }
      };
      const tick = () => {
        if (stopped || !engine.ctx) return;
        const elapsed = engine.ctx.currentTime - t0;
        for (const cue of cues) {
          if (cue.done || elapsed < cue.at) continue;
          cue.done = true;
          if (cue.caption) setCaption(cue.caption);
          if (cue.release) {
            engine.release();
            setActive(new Set());
          }
          if (cue.note != null) {
            engine.noteOn(midiToFreq(cue.note), legatoRef.current && !!cue.legato, effortNow);
            setActive(new Set([cue.note]));
          }
          if (cue.effort != null || cue.mute != null) {
            ramp = {
              fromE: effortNow,
              fromM: muteNow,
              toE: cue.effort ?? effortNow,
              toM: cue.mute ?? muteNow,
              at: elapsed,
              dur: Math.max(0.02, cue.dur ?? 0.04),
            };
          }
        }
        const t = clamp((elapsed - ramp.at) / ramp.dur, 0, 1);
        effortNow = ramp.fromE + (ramp.toE - ramp.fromE) * t;
        muteNow = ramp.fromM + (ramp.toM - ramp.fromM) * t;
        engine.setEffort(effortNow);
        engine.setMute(muteNow);
        setEffort(effortNow);
        setMute(muteNow);
        if (elapsed >= DEMO_LENGTH) finish();
        else frame = requestAnimationFrame(tick);
      };
      demoStop.current = finish;
      setDemoOn(true);
      frame = requestAnimationFrame(tick);
    })();
  };

  const shiftOctave = (dir: -1 | 1) => {
    if (narrow) {
      setNarrowStart((current) => {
        const index = NARROW_STARTS.indexOf(current as (typeof NARROW_STARTS)[number]);
        return NARROW_STARTS[clamp(index + dir, 0, NARROW_STARTS.length - 1)] ?? current;
      });
      return;
    }
    setBase((current) => {
      const index = OCTAVE_BASES.indexOf(current as (typeof OCTAVE_BASES)[number]);
      const next = clamp(index + dir, 0, OCTAVE_BASES.length - 1);
      return OCTAVE_BASES[next] ?? current;
    });
  };

  const keysStart = narrow ? narrowStart : base;
  const keysCount = narrow ? 13 : 24;

  return (
    <main className="horn-page">
      <section
        ref={caseRef}
        className="horn-case"
        data-speaking={speaking ? "true" : "false"}
        style={{ ["--mute" as string]: mute.toFixed(3) }}
      >
        <header className="flex items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Imaginary horn</p>
            <h1 className="horn-title">Gloam</h1>
          </div>
          <div className="header-actions">
            {!narrow ? (
              <div className="voice-toggle" role="radiogroup" aria-label="Voice model">
                {(["placed", "lip"] as const).map((mode) => {
                  const on = voiceMode === mode;
                  return (
                    <button
                      key={mode}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      tabIndex={on ? 0 : -1}
                      data-voice-mode={mode}
                      className={on ? "voice-opt voice-opt-on" : "voice-opt"}
                      onClick={() => chooseVoiceMode(mode)}
                      onKeyDown={(event) => {
                        if (
                          event.key !== "ArrowLeft" &&
                          event.key !== "ArrowRight" &&
                          event.key !== "ArrowUp" &&
                          event.key !== "ArrowDown"
                        )
                          return;
                        event.preventDefault();
                        const next = mode === "placed" ? "lip" : "placed";
                        chooseVoiceMode(next);
                        event.currentTarget.parentElement
                          ?.querySelector<HTMLButtonElement>(`[data-voice-mode="${next}"]`)
                          ?.focus();
                      }}
                    >
                      {mode === "placed" ? "Placed lip" : "Self-oscillating lip"}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {!narrow ? <PlayModeToggle mode={playMode} onChange={choosePlayMode} /> : null}
            {!narrow ? <LegatoToggle on={legato} onToggle={chooseLegato} /> : null}
            {!narrow ? <CharacterControl value={character} onChange={applyCharacter} /> : null}
            <button type="button" className="phrase-btn shrink-0" onClick={playDemo}>
              {demoOn ? <Square size={16} aria-hidden /> : <Play size={16} aria-hidden />}
              {demoOn ? "Stop" : "Play a phrase"}
            </button>
          </div>
        </header>

        <div className="preset-grid" role="radiogroup" aria-label="Starting bores">
          {PRESET_ORDER.map((id) => {
            const preset = PRESETS[id];
            const on = id === presetId;
            return (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={on}
                tabIndex={on ? 0 : -1}
                onKeyDown={(event) => {
                  const index = PRESET_ORDER.indexOf(id);
                  const step =
                    event.key === "ArrowRight" || event.key === "ArrowDown"
                      ? 1
                      : event.key === "ArrowLeft" || event.key === "ArrowUp"
                        ? -1
                        : 0;
                  if (!step && event.key !== "Home" && event.key !== "End") return;
                  event.preventDefault();
                  const next =
                    event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? PRESET_ORDER.length - 1
                        : (index + step + PRESET_ORDER.length) % PRESET_ORDER.length;
                  choosePreset(PRESET_ORDER[next]);
                  const buttons =
                    event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                      '[role="radio"]',
                    );
                  buttons?.[next]?.focus();
                }}
                className={on ? "preset preset-on" : "preset"}
                onClick={() => choosePreset(id)}
              >
                {favorites[id] ? <span className="preset-saved">Saved</span> : null}
                <span className="preset-name">{preset.name}</span>
                <span className="preset-blurb">{preset.blurb}</span>
              </button>
            );
          })}
        </div>

        <div className="favorite-row">
          <button
            type="button"
            className="midi-btn fav-btn"
            onClick={saveFavorite}
            aria-label={`Save air and mute as the ${PRESETS[presetId].name} favorite`}
          >
            Save favorite
          </button>
          <button
            type="button"
            className="midi-btn fav-btn"
            onClick={recallFavorite}
            disabled={!favorites[presetId]}
            aria-label={`Recall the ${PRESETS[presetId].name} favorite`}
          >
            Recall
          </button>
          <ShadeControl presetId={presetId} value={shade} onChange={applyShade} />
          <span className="favorite-status">
            {favorites[presetId]
              ? `Air ${Math.round(favorites[presetId].effort * 100)} · mute ${Math.round(favorites[presetId].mute * 100)} · ${SHADE_LABEL[presetId]} ${Math.round(favorites[presetId].shade * 100)}`
              : "Uses the preset home"}
          </span>
        </div>

        <div className="horn-grid">
          <div className="expression-sliders">
            <div className="expression-slider">
              <label htmlFor="gloam-air">Air</label>
              <input
                id="gloam-air"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={effort}
                aria-valuetext={`Air ${Math.round(effort * 100)}`}
                onPointerDown={onSliderPointerDown}
                onChange={onAirSlider}
              />
              <output htmlFor="gloam-air">{Math.round(effort * 100)}</output>
            </div>
            <div className="expression-slider">
              <label htmlFor="gloam-mute">Mute</label>
              <input
                id="gloam-mute"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={mute}
                aria-valuetext={`Mute ${Math.round(mute * 100)}`}
                onPointerDown={onSliderPointerDown}
                onChange={onMuteSlider}
              />
              <output htmlFor="gloam-mute">{Math.round(mute * 100)}</output>
            </div>
          </div>

          <aside className="readout">
            <p className="caption" aria-live="polite">
              {caption}
            </p>
            <dl className="meters">
              <div>
                <dt>Air</dt>
                <dd>{Math.round(breathWithPressure(effort, pressure) * 100)}</dd>
              </div>
              <div>
                <dt>Mute</dt>
                <dd>{Math.round(mute * 100)}</dd>
              </div>
              <div>
                <dt>Note</dt>
                <dd>{currentName(active) ?? "—"}</dd>
              </div>
              <div>
                <dt>Bore</dt>
                <dd>{PRESETS[presetId].name}</dd>
              </div>
            </dl>
            {/* Open beside the sliders on wide screens; folded on phones so the keys sit under them. */}
            <details className="model" open={!narrow}>
              <summary className="model-kicker">The model</summary>
              <p className="model-copy">
                A hybrid, not a sample and not a real trumpet. Pitch is an embouchure — a lip you
                place on the note. The lip is a nonlinear valve, in the spirit of Cook’s brass
                model, driving a tuned bore that stores the air. Effort changes how hard that valve
                clips and how fast it speaks, so the spectrum blooms instead of only getting louder.
                Mute damps the tube, lifts a nasal peak, and sharpens the note a little. Soft
                entrances scoop, slurs keep the air moving, and                 releases leave the bore ringing.
                {playMode === "poly"
                  ? " Poly keeps four of those horns ready. A chord mixes under the same soft knee, and one note stays at the mono level."
                  : ""}
              </p>
            </details>
          </aside>
        </div>

        <div className="keyboard-scroll">
          <Keyboard
            base={base}
            start={keysStart}
            count={keysCount}
            active={active}
            onNoteDown={beginGlide}
            onKeyNote={keyNote}
          />
        </div>

        <div className="transport">
          <button
            type="button"
            className="octave-btn"
            disabled={keysStart === 36}
            onPointerUp={(event) => {
              if (event.pointerType !== "touch") return;
              armOctaveTouch();
              shiftOctave(-1);
            }}
            onClick={() => {
              if (octaveFromTouch.current) {
                octaveFromTouch.current = false;
                return;
              }
              shiftOctave(-1);
            }}
          >
            <Minus size={16} aria-hidden className="mr-1 inline" />
            Lower
          </button>
          <p className="range-label">
            {noteName(keysStart)} – {noteName(keysStart + keysCount - 1)}
          </p>
          <button
            type="button"
            className="octave-btn"
            disabled={keysStart === (narrow ? 72 : 60)}
            onPointerUp={(event) => {
              if (event.pointerType !== "touch") return;
              armOctaveTouch();
              shiftOctave(1);
            }}
            onClick={() => {
              if (octaveFromTouch.current) {
                octaveFromTouch.current = false;
                return;
              }
              shiftOctave(1);
            }}
          >
            Higher
            <Plus size={16} aria-hidden className="ml-1 inline" />
          </button>
            <button
              type="button"
              className="octave-btn"
              onClick={releaseAll}
              title="Release every note and stop the phrase (Escape)"
            >
              Silence
            </button>
            {slur && legato ? <span className="slur-pill">Slur held</span> : null}
        </div>
        <p className="help">
          {narrow ? (
            <>
              Slide a finger along the keys to glide; a second finger slurs. Air is how hard you
              blow; Mute covers the bell. Lower and Higher change the octave.
            </>
          ) : (
            <>
              Computer keys play two octaves; minus and equals change their range. Air is how hard
              you blow; Mute covers the bell. Arrow keys nudge either one, and each slider moves on
              its own when focused. Space holds a slur so notes connect. Overlapping notes slur on
              their own. Escape silences everything.
            </>
          )}
          {playMode === "poly" ? (
            <>
              {" "}
              Poly holds four notes on either lip — each its own lip and bore. Air, mute, character,
              the bore's own control, and bend move them together, and the oldest note gives way.
            </>
          ) : null}
        </p>
        <div className="expression-settings">
          {narrow ? (
            <div className="voice-toggle" role="radiogroup" aria-label="Voice model">
              {(["placed", "lip"] as const).map((mode) => {
                const on = voiceMode === mode;
                return (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    tabIndex={on ? 0 : -1}
                    data-voice-mode={mode}
                    className={on ? "voice-opt voice-opt-on" : "voice-opt"}
                    onClick={() => chooseVoiceMode(mode)}
                    onKeyDown={(event) => {
                      if (
                        event.key !== "ArrowLeft" &&
                        event.key !== "ArrowRight" &&
                        event.key !== "ArrowUp" &&
                        event.key !== "ArrowDown"
                      )
                        return;
                      event.preventDefault();
                      const next = mode === "placed" ? "lip" : "placed";
                      chooseVoiceMode(next);
                      event.currentTarget.parentElement
                        ?.querySelector<HTMLButtonElement>(`[data-voice-mode="${next}"]`)
                        ?.focus();
                    }}
                  >
                    {mode === "placed" ? "Placed lip" : "Self-oscillating lip"}
                  </button>
                );
              })}
            </div>
          ) : null}
          {narrow ? <PlayModeToggle mode={playMode} onChange={choosePlayMode} /> : null}
          {narrow ? <LegatoToggle on={legato} onToggle={chooseLegato} /> : null}
          {narrow ? <CharacterControl value={character} onChange={applyCharacter} /> : null}
          {!audioReady ? (
            <button type="button" className="midi-btn" onClick={() => void wake()}>
              Enable audio
            </button>
          ) : null}
          <label htmlFor="gloam-volume">
            Output <output>{Math.round(volume * 100)}%</output>
          </label>
          <input
            id="gloam-volume"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(event) => setVolume(Number(event.target.value))}
          />
          <button type="button" className="midi-btn" onClick={resetExpression}>
            Reset expression
          </button>
          {midiSupported ? (
            <div className="midi-row">
              {midi.state === "on" && midi.names.length > 0 ? (
                <span className="midi-status">MIDI: {midi.names.join(", ")}</span>
              ) : midi.state === "on" ? (
                <span className="midi-status">MIDI: plug in a device</span>
              ) : (
                <button
                  type="button"
                  className="midi-btn"
                  disabled={midi.state === "pending"}
                  onClick={() => void connectMidi()}
                >
                  {midi.state === "denied"
                    ? "Retry MIDI permission"
                    : midi.state === "pending"
                      ? "Connecting MIDI…"
                      : "Connect MIDI"}
                </button>
              )}
            </div>
          ) : null}
        </div>
        <p className="settings-note">
          {storageAvailable
            ? "Bore, voice, legato, play mode, character, favorites, expression, output and ranges are remembered on this browser."
            : "Browser storage is unavailable; controls will last for this visit."}
        </p>
        {midi.state === "on" ? (
          <p className="settings-note">
            All MIDI channels · velocity scales breath and attack · CC2/11 = air · pressure adds air ·
            CC1 = mute · CC21 = character (0 horn, 64 center, 127 bass) · CC64 = sustain · bend ±2
            semitones
            {playMode === "poly" ? " · poly bend is shared by every note" : ""}. Notes outside the
            visible range still play.
          </p>
        ) : null}
        {audioError ? (
          <p className="audio-error" role="alert">
            {audioError}{" "}
            <button type="button" className="midi-btn" onClick={() => void wake()}>
              Retry audio
            </button>
          </p>
        ) : null}
      </section>
    </main>
  );
}

function currentName(active: ReadonlySet<number>) {
  let last: number | null = null;
  for (const midi of active) last = midi;
  return last == null ? null : noteName(last);
}
